import { Alert } from '@creditornot/cb-alert';
import { TextButton } from '@creditornot/cb-button';
import { cssThemedValue } from '@creditornot/cb-ingredients';
import React, { FC, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Typography as T } from 'common/typography';

import { LookerDashboardView } from './components/LookerDashboardView';
import { useHandleLookerEvents } from './hooks/useHandleLookerEvents';
import useLookerDashboards from './hooks/useLookerDashboards';
import { telemetryHistoricalVenueAvailabilityView } from './telemetry';
import { Entity } from './types';

type Props = {
  entity: Entity;
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
`;

const Header = styled.div`
  padding: 32px;
`;

const Divider = styled.hr`
  border: none;
  border-block-end: 1px solid ${cssThemedValue('borderPrimary')};
  margin: 0;
`;

const StyledAlert = styled(Alert)`
  border-radius: 0;
`;

const StyledIframe = styled.iframe`
  width: 100%;
  border: none;
  flex: 1 1 auto;
`;

const HistoricalVenueAvailbility: FC<Props> = ({ entity }) => {
  useEffect(() => {
    telemetryHistoricalVenueAvailabilityView();
  }, []);
  const {
    data: dashboardUrls,
    isLoading,
    error: fetchDashboardsError,
    showLookerUrlAboutToExpireWarning,
    refetchLookerUrls,
  } = useLookerDashboards(entity);
  const iframeElArr = useRef<HTMLIFrameElement[]>([]);
  const { hasLoadDashboardInIframeError } = useHandleLookerEvents(iframeElArr.current);

  const lookerDashboardViewProps = {
    isLoading,
    hasFetchDashboardError: !!fetchDashboardsError || !dashboardUrls?.length,
    hasLoadDashboardInIframeError,
  };
  return (
    <Root>
      <Header>
        <T.Heading2>
          <FormattedMessage id="historical-venue-availability.title" />
        </T.Heading2>
      </Header>
      <Divider />
      <LookerDashboardView {...lookerDashboardViewProps}>
        {showLookerUrlAboutToExpireWarning && (
          <StyledAlert variant="warning" size="small">
            <FormattedMessage
              id="looker-analytics.auth-warning"
              values={{
                action: (str: string) => (
                  <TextButton size="inherit" onClick={refetchLookerUrls}>
                    {str}
                  </TextButton>
                ),
              }}
            />
          </StyledAlert>
        )}
        {dashboardUrls?.map((url, index) => (
          <StyledIframe
            ref={el => {
              if (el) {
                iframeElArr.current[index] = el;
              }
            }}
            key={url}
            src={url}
            data-testid="historical-availability-iframe"
          />
        ))}
      </LookerDashboardView>
    </Root>
  );
};

export default HistoricalVenueAvailbility;
